import type { KeyboardEvent, ReactNode } from 'react';

type PlanCardShellProps = {
  gradientClass: string;
  children: ReactNode;
  /** When set with onClick, card acts as a selectable plan tile. */
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  /** Optional badge pinned above the card (e.g. “Most Popular”). */
  badge?: ReactNode;
  /** Tailwind ring color class when `selected` (default emerald). */
  selectedRingClassName?: string;
  /** Minimum height for the card body area. */
  minHeightClassName?: string;
  /** Classes for the inner content wrapper (padding / gap). */
  innerClassName?: string;
};

/**
 * Shared “modern” plan card shell: gradient top bar, white body, soft shadow (admin + profile).
 */
export function PlanCardShell({
  gradientClass,
  children,
  selected = false,
  onClick,
  disabled = false,
  className = '',
  badge,
  selectedRingClassName = 'ring-emerald-500',
  minHeightClassName = 'min-h-[300px]',
  innerClassName = 'gap-3 p-6 sm:gap-4 sm:p-7',
}: PlanCardShellProps) {
  const interactive = Boolean(onClick) && !disabled;

  const onKeyDown = interactive
    ? (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }
    : undefined;

  return (
    <div className={`relative shrink-0 ${badge ? 'pt-3' : ''}`}>
      {badge ? (
        <div className="pointer-events-none absolute left-1/2 top-0 z-10 max-w-[min(100%,24rem)] -translate-x-1/2 -translate-y-1/2 px-1">
          <div className="flex flex-wrap items-center justify-center gap-2">{badge}</div>
        </div>
      ) : null}
      <div
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-pressed={interactive ? selected : undefined}
        onClick={interactive ? onClick : undefined}
        onKeyDown={onKeyDown}
        className={`group relative flex ${minHeightClassName} flex-col overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-6px_rgba(0,0,0,0.08)] transition-all duration-200 ${
          selected
            ? `ring-2 ${selectedRingClassName} shadow-[0_4px_16px_-4px_rgba(16,185,129,0.15)]`
            : 'ring-1 ring-gray-950/[0.06] hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.06),0_20px_40px_-12px_rgba(0,0,0,0.12)]'
        } ${interactive ? 'cursor-pointer' : 'cursor-default'} ${disabled ? 'pointer-events-none opacity-55' : ''} ${className}`}
      >
        <div className={`h-1.5 w-full bg-gradient-to-r ${gradientClass}`} aria-hidden />
        <div className={`flex flex-1 flex-col ${innerClassName}`}>{children}</div>
      </div>
    </div>
  );
}

type PlanMetricRowProps = {
  label: string;
  suffix: string;
  children: ReactNode;
  /** Extra classes on the middle cell (e.g. focus ring for inputs). */
  middleClassName?: string;
};

export function PlanMetricRow({ label, suffix, children, middleClassName = '' }: PlanMetricRowProps) {
  const suffixText = suffix.trim();
  return (
    <div className="flex w-full items-stretch gap-2.5 sm:gap-3">
      <span className="flex w-[38%] shrink-0 items-center text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 sm:max-w-[9.5rem] leading-snug">
        {label}
      </span>
      <div
        className={`flex min-w-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-gray-50/90 px-2 py-2 text-center ring-1 ring-inset ring-gray-200/70 ${middleClassName}`}
      >
        <span className="block min-w-0 max-w-full text-center leading-tight">{children}</span>
      </div>
      {suffixText ? (
        <span className="flex w-11 shrink-0 items-center justify-end text-[11px] font-medium tabular-nums text-gray-400 sm:w-14">
          {suffix}
        </span>
      ) : null}
    </div>
  );
}
